import type { CatalogTransport } from './warehouse-catalog-client';
export const AR_BUCKETS = ['Current', '1-30', '31-60', '61-90', '90+'] as const;
export type ArRow = { transaction_id: string; document_number: string; transaction_type: string; balance_class: 'receivable' | 'deposit_liability'; customer: string | null;
  document_date: string | null; due_date: string | null; currency: string; invoice_amount: number; open_amount: number;
  days_outstanding: number | null; aging_basis: string; bucket: string | null };
export type ArData = { rows: ArRow[]; asOf: string; fetchedAt: number };
export type ArResult = { state: 'loading' | 'unauthenticated' | 'denied' | 'error' } | { state: 'ready'; data: ArData };
const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0 && v.length <= 500;
const date = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)
  && Number.isFinite(Date.parse(v + 'T00:00:00Z')) && new Date(v + 'T00:00:00Z').toISOString().slice(0, 10) === v;
/** Current balances only. No historical date, currency translation or incomplete population. */
export function parseArAging(value: unknown): ArData {
  if (!record(value) || value.source !== 'netsuite' || value.view !== 'ar_aging_v2' || !record(value.deposit_summary)
    || value.truncated !== false || value.limit_applied !== false || !date(value.as_of)
    || value.balance_as_of !== value.as_of || !Number.isSafeInteger(value.fetchedAt) || (value.fetchedAt as number) <= 0
    || (value.fetchedAt as number) > 253402300799 || !Array.isArray(value.rows) || value.rows.length > 20000
    || value.row_count !== value.rows.length || value.fetched_row_count !== value.rows.length
    || value.total_results !== value.rows.length || JSON.stringify(value.buckets) !== JSON.stringify(AR_BUCKETS)) throw new Error('Unusable aging population');
  const seen = new Set<string>();
  const rows = value.rows.map(row => {
    if (!record(row) || !text(row.transaction_id) || seen.has(row.transaction_id) || !text(row.document_number)
      || !['CustInvc', 'CustCred', 'CustPymt', 'CustDep'].includes(String(row.transaction_type))
      || row.balance_class !== (row.transaction_type === 'CustDep' ? 'deposit_liability' : 'receivable')
      || !(row.customer == null || text(row.customer)) || !/^[A-Z]{3}$/.test(String(row.currency))
      || typeof row.invoice_amount !== 'number' || !Number.isFinite(row.invoice_amount)
      || typeof row.open_amount !== 'number' || !Number.isFinite(row.open_amount)
      || ![row.document_date, row.due_date].every(v => v == null || typeof v === 'string')
      || !['due_date', 'document_date', 'unparseable_date'].includes(String(row.aging_basis))) throw new Error('Invalid aging row');
    const days = row.days_outstanding;
    if (days === null ? row.bucket !== null || row.aging_basis !== 'unparseable_date'
      : !Number.isSafeInteger(days) || row.aging_basis === 'unparseable_date'
        || row.bucket !== ((days as number) <= 0 ? 'Current' : (days as number) <= 30 ? '1-30' : (days as number) <= 60 ? '31-60' : (days as number) <= 90 ? '61-90' : '90+')) throw new Error('Invalid aging bucket');
    seen.add(row.transaction_id);
    return { ...row, customer: row.customer ?? null, document_date: row.document_date ?? null, due_date: row.due_date ?? null } as unknown as ArRow;
  });
  return { rows, asOf: value.as_of, fetchedAt: value.fetchedAt as number };
}
export function filterArRows(rows: ArRow[], currency: string, bucket: string, search: string) {
  const query = search.trim().toLowerCase();
  return rows.filter(row => row.balance_class === 'receivable' && row.currency === currency && (bucket === 'all' || (bucket === 'unavailable' ? row.bucket === null : row.bucket === bucket))
    && `${row.customer ?? ''} ${row.document_number}`.toLowerCase().includes(query));
}
/** Deposits share currency/search filters, but have no AR aging bucket. */
export function filterDepositRows(rows: ArRow[], currency: string, search: string) {
  const query = search.trim().toLowerCase();
  return rows.filter(row => row.balance_class === 'deposit_liability' && row.currency === currency
    && `${row.customer ?? ''} ${row.document_number}`.toLowerCase().includes(query));
}
/** One selected transaction currency; a display sum of the filtered source rows. */
export function arTotals(rows: ArRow[]) {
  rows = rows.filter(row => row.balance_class === 'receivable');
  if (new Set(rows.map(row => row.currency)).size > 1) throw new Error('Mixed currencies');
  const debit = rows.reduce((sum, row) => sum + Math.max(0, row.open_amount), 0);
  const credit = rows.reduce((sum, row) => sum + Math.min(0, row.open_amount), 0);
  if (![debit, credit, debit + credit].every(Number.isFinite)) throw new Error('Non-finite aging total');
  return { debit, credit, net: debit + credit };
}
export function depositTotals(rows: ArRow[]) {
  const deposits = rows.filter(row => row.balance_class === 'deposit_liability');
  if (new Set(deposits.map(row => row.currency)).size > 1) throw new Error('Mixed currencies');
  return { amount: deposits.reduce((sum, row) => sum + row.open_amount, 0), count: deposits.length };
}
export function createArAgingLoader(transport: CatalogTransport, publish: (state: ArResult) => void) {
  let generation = 0; let disposed = false; let controller: AbortController | undefined;
  return {
    async refresh() {
      if (disposed) return;
      const request = ++generation; controller?.abort(); controller = new AbortController(); publish({ state: 'loading' });
      let result: ArResult;
      try {
        const response = await transport('/api/finance/ar-aging', { method: 'GET', credentials: 'same-origin', cache: 'no-store',
          redirect: 'error', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (response.status === 401) result = { state: 'unauthenticated' };
        else if (response.status === 403) result = { state: 'denied' };
        else if (!response.ok || response.redirected || !/^application\/json(?:;|$)/i.test(response.headers.get('Content-Type') ?? '')) result = { state: 'error' };
        else result = { state: 'ready', data: parseArAging(await response.json()) };
      } catch { result = { state: 'error' }; }
      if (!disposed && request === generation) publish(result);
    },
    dispose() { disposed = true; generation++; controller?.abort(); },
  };
}
